import { __read, __spreadArray } from "tslib";
import { convertActivityTypeToActivityReason } from '@office-start/activity-utility';
import { ActivityReason, AttachmentType, DocumentActivityType, FileType, } from '@office-start/document-types';
import { getFileAttributionUrl, getFileExtensionFromUrl, getFileTypeFromExtension, getReferralFromFileLocation, } from '@office-start/file-utility';
import { getFormatTimeFromDate } from '@office-start/loc-service';
import { ActionType, } from '@office-start/recommended-types';
import { format } from '@office-start/string-formatter-utility';
import { serverRequestIdHeader } from '../constants';
import { getOdspShareInfo } from '../util/odspShareUtilities';
function convertReasonTypeToActivityReason(type) {
    var enumType = DocumentActivityType[type];
    return convertActivityTypeToActivityReason(enumType);
}
function convertISODurationToMediaDuration(isoDuration) {
    var mediaDuration = undefined;
    var mediaDurationRegex = new RegExp(/PT(?:(\d+)H)?(?:(\d+)M)?(\d*).*S?/); //regex for getting video length
    var regResult = mediaDurationRegex.exec(isoDuration || '');
    if (regResult) {
        // This assumes we can only have hours/minutes/seconds
        var hours = regResult[1];
        var minutes = regResult[2];
        var seconds = regResult[3];
        mediaDuration = "".concat(hours ? hours.padStart(2, '0') + ':' : '').concat(minutes ? minutes.padStart(2, '0') + ':' : '').concat(seconds.padStart(2, '0') || '');
    }
    return mediaDuration && mediaDuration.length <= 2
        ? '00:' + mediaDuration
        : mediaDuration;
}
function convertOfficeFeedActivityToActivityDescription(activity) {
    var formatString = activity.reason || '';
    var peopleFormat = [];
    var people = [];
    if (activity.actors && activity.actors.length > 0) {
        // For filetype other than Video, actors is the array used to store user information
        peopleFormat = activity.actors.map(function (user) { return user.User.DisplayName; });
        people = (activity.actors || []).map(function (user, index) {
            return {
                upn: user && user.User.email,
                name: user.User.DisplayName,
                tag: index.toString(),
            };
        });
    }
    else if (activity.sharedBy) {
        // The actors array for Video is empty and the sharedBy property is used to store the user
        people.push({
            upn: activity.sharedBy.User.email,
            name: activity.sharedBy.User.DisplayName,
            tag: activity.sharedBy.User.email,
        });
        peopleFormat.push(activity.sharedBy.User.DisplayName);
    }
    return {
        formatString: formatString,
        plainText: format.apply(void 0, __spreadArray([activity.reason], __read(peopleFormat), false)),
        people: people,
        reasonType: activity.kind === 'popularVideoInclusionReason'
            ? ActivityReason.Trending
            : activity.kind === 'meetingRecordingInclusionReason'
                ? ActivityReason.Video
                : convertReasonTypeToActivityReason(activity.inclusionKind),
        timestamp: activity.dateTime ? new Date(activity.dateTime) : undefined,
        id: '0', // TODO: Not sure if this is needed
    };
}
// TODO - Localization
function getInsightDescriptionPrefix(actionType) {
    var insightDescriptionPrefix = 'You said';
    if (actionType === ActionType.FollowUp ||
        actionType === ActionType.FollowUpActionRequest ||
        actionType === ActionType.FollowUpReplyRequest) {
        insightDescriptionPrefix = 'You asked';
    }
    else if (actionType === ActionType.Commitment) {
        insightDescriptionPrefix = 'You said to';
    }
    else if (actionType === ActionType.Request) {
        insightDescriptionPrefix = 'They asked';
    }
    else if (actionType === ActionType.ActionRequest ||
        actionType === ActionType.ReplyRequest) {
        insightDescriptionPrefix = ' asked You';
    }
    return insightDescriptionPrefix;
}
function getDeepLinkUrl(webUrl, navId) {
    if (webUrl && navId) {
        var deepLinkUrl = new URL(webUrl);
        deepLinkUrl.searchParams.append('nav', navId);
        return deepLinkUrl.toString();
    }
    return '';
}
export function convertRawDataToRecommendedFileItem(rawItem) {
    var _a, _b, _c, _d, _e, _f;
    var itemTimestamp = rawItem.lastModifiedDateTime
        ? new Date(rawItem.lastModifiedDateTime)
        : undefined;
    var sharePointIds = rawItem.sharePointIds;
    var sharepointInfo = sharePointIds && {
        listId: sharePointIds.listId,
        listItemId: '',
        listItemUniqueId: sharePointIds.listItemUniqueId,
        siteId: sharePointIds.siteId,
        siteUrl: rawItem.sharePointDetails && rawItem.sharePointDetails.sitePath,
        webId: sharePointIds.webId,
    };
    var inclusionReason = rawItem.inclusionReasons && rawItem.inclusionReasons[0];
    var activityDescription = undefined;
    var isEmailAttachment = false;
    var emailReferenceData;
    var navigationId = undefined;
    if (inclusionReason) {
        activityDescription =
            convertOfficeFeedActivityToActivityDescription(inclusionReason);
        isEmailAttachment = inclusionReason.inclusionKind === 'EmailAttachment';
        if (isEmailAttachment) {
            emailReferenceData = {
                AttachmentType: AttachmentType.Classic,
                ConversationId: '',
                Subject: inclusionReason.conversationSubject || '',
            };
        }
        navigationId = inclusionReason.navigationId;
    }
    // isOneDriveForBusiness and isSharePointFile are used as inputs to getOdspShareInfo
    // which determines whether or not this piece of content can be shared via the ODSP Share Dialog
    var referralType = getReferralFromFileLocation(rawItem.uri);
    var isOneDriveForBusinessFile = referralType === "Documents" /* KnownWorkloadReferralNames.OneDrive */;
    var isSharePointFile = referralType === "Sites" /* KnownWorkloadReferralNames.SharePoint */;
    var url = isEmailAttachment && rawItem.webUrl ? rawItem.webUrl : rawItem.uri;
    var webUrl = rawItem.webUrl;
    var deepLinkUrl = getDeepLinkUrl(webUrl, navigationId);
    var document = {
        activity: activityDescription,
        app: (_a = rawItem.appInfo) === null || _a === void 0 ? void 0 : _a.name,
        attribution: rawItem.uri,
        attributionUrl: getFileAttributionUrl(rawItem.uri),
        canonicalUrl: rawItem.uri || rawItem.webUrl,
        docId: (_b = rawItem.oneDriveItemInfo) === null || _b === void 0 ? void 0 : _b.itemId,
        downloadUrl: rawItem.uri,
        driveId: (_c = rawItem.oneDriveItemInfo) === null || _c === void 0 ? void 0 : _c.driveId,
        emailReference: emailReferenceData,
        extension: ((_d = rawItem.file) === null || _d === void 0 ? void 0 : _d.extension) || getFileExtensionFromUrl(rawItem.uri),
        feedbackId: rawItem.feedbackId,
        fileName: rawItem.name,
        fileType: getFileTypeFromExtension(((_e = rawItem.file) === null || _e === void 0 ? void 0 : _e.extension) || getFileExtensionFromUrl(rawItem.webUrl)),
        id: rawItem.itemId,
        isCollab: rawItem.is_collab,
        isEmailAttachment: isEmailAttachment,
        isOneDriveForBusinessFile: isOneDriveForBusinessFile,
        isSharePointFile: isSharePointFile,
        lastAccessedTime: itemTimestamp,
        mediaDuration: convertISODurationToMediaDuration((_f = rawItem.video) === null || _f === void 0 ? void 0 : _f.mediaDuration),
        actionInfo: {
            odspShareInfo: getOdspShareInfo({
                isOneDriveForBusinessFile: isOneDriveForBusinessFile,
                isSharePointFile: isSharePointFile,
                itemId: rawItem.itemId,
                sharepointInfo: sharepointInfo,
                url: url,
                webUrl: webUrl,
            }),
        },
        resourceId: rawItem.resource_id,
        sharepointIds: sharepointInfo,
        title: rawItem.name,
        url: url,
        webUrl: webUrl,
        deepLinkUrl: deepLinkUrl,
        navId: navigationId,
    };
    return document;
}
export function convertRawDataToRecommendedUpcomingMeeting(rawItem, officeMarket) {
    var _a, _b, _c, _d, _e, _f;
    var upcomingMeetingInfo = rawItem.upcomingMeetingInfo;
    var isTeamsMeeting = ((_a = upcomingMeetingInfo === null || upcomingMeetingInfo === void 0 ? void 0 : upcomingMeetingInfo.onlineMeeting) === null || _a === void 0 ? void 0 : _a.onlineMeetingProvider) ===
        'TeamsForBusiness';
    var isUpcomingMeetingAccepted = rawItem.itemGroups &&
        rawItem.itemGroups.length > 0 &&
        rawItem.itemGroups.indexOf('MeetingOnlineAttending') !== -1;
    var activityDescription = {
        id: '0',
        formatString: '',
        plainText: '',
        people: [],
        reasonType: ActivityReason.Meeting,
        timestamp: upcomingMeetingInfo && new Date(upcomingMeetingInfo.start),
    };
    // Do not add Upcoming meeting as recommended card if timestamp is beyond the threshold
    var expiresAt = (rawItem.expiresAt && new Date(rawItem.expiresAt)) || null;
    var currentTime = new Date();
    if (expiresAt && currentTime > expiresAt) {
        return undefined;
    }
    var document = {
        app: (_b = rawItem.appInfo) === null || _b === void 0 ? void 0 : _b.name,
        attribution: rawItem.uri,
        docId: undefined,
        driveId: undefined,
        extension: (_c = rawItem.file) === null || _c === void 0 ? void 0 : _c.extension,
        id: rawItem.itemId,
        title: ((_d = rawItem.previewInfo) === null || _d === void 0 ? void 0 : _d.text) || '',
        url: rawItem.webUrl,
        webUrl: rawItem.webUrl,
        lastAccessedTime: (upcomingMeetingInfo && new Date(upcomingMeetingInfo.start)) ||
            undefined,
        fileType: FileType.UpcomingMeeting,
        pathArray: undefined,
        resourceId: rawItem.resource_id,
        isCollab: rawItem.is_collab,
        canonicalUrl: rawItem.uri || rawItem.webUrl,
        feedbackId: rawItem.feedbackId,
        attributionUrl: getFileAttributionUrl(rawItem.uri),
        activity: activityDescription,
        attendees: upcomingMeetingInfo && upcomingMeetingInfo.attendees,
        subject: upcomingMeetingInfo === null || upcomingMeetingInfo === void 0 ? void 0 : upcomingMeetingInfo.subject,
        joinUrl: (_e = upcomingMeetingInfo === null || upcomingMeetingInfo === void 0 ? void 0 : upcomingMeetingInfo.onlineMeeting) === null || _e === void 0 ? void 0 : _e.joinUrl,
        meetingStart: upcomingMeetingInfo &&
            getFormatTimeFromDate(new Date(upcomingMeetingInfo === null || upcomingMeetingInfo === void 0 ? void 0 : upcomingMeetingInfo.start), officeMarket),
        meetingEnd: upcomingMeetingInfo &&
            getFormatTimeFromDate(new Date(upcomingMeetingInfo === null || upcomingMeetingInfo === void 0 ? void 0 : upcomingMeetingInfo.end), officeMarket),
        location: (_f = upcomingMeetingInfo === null || upcomingMeetingInfo === void 0 ? void 0 : upcomingMeetingInfo.location) === null || _f === void 0 ? void 0 : _f.displayName,
        organizer: upcomingMeetingInfo === null || upcomingMeetingInfo === void 0 ? void 0 : upcomingMeetingInfo.organizer,
        isUpcomingMeetingAccepted: isUpcomingMeetingAccepted,
        isTeamsMeeting: isTeamsMeeting,
        documents: convertMeetingObjectDataToRecommendedMeetingObjects(upcomingMeetingInfo === null || upcomingMeetingInfo === void 0 ? void 0 : upcomingMeetingInfo.documents),
    };
    return document;
}
function convertMeetingObjectDataToRecommendedMeetingObjects(rawObjects) {
    var objects = rawObjects === null || rawObjects === void 0 ? void 0 : rawObjects.map(function (rawObject) {
        var itemTimestamp = rawObject.lastModifiedDateTime
            ? new Date(rawObject.lastModifiedDateTime)
            : undefined;
        var sharePointIds = rawObject.sharePointIds;
        var sharepointInfo = sharePointIds && {
            listId: sharePointIds.listId,
            listItemId: '',
            listItemUniqueId: sharePointIds.listItemUniqueId,
            siteId: sharePointIds.siteId,
            siteUrl: '',
            webId: sharePointIds.webId,
        };
        var object = {
            app: getFileTypeFromExtension(rawObject.extension),
            attribution: rawObject.webUrl,
            contentSource: rawObject.contentSource,
            fileName: rawObject.name,
            extension: rawObject.extension,
            title: rawObject.title,
            mediaType: rawObject.mediaType,
            sharepointIds: sharepointInfo,
            size: rawObject.size,
            id: rawObject.id,
            inclusionReason: rawObject.inclusionReason,
            url: rawObject.webUrl,
            webUrl: rawObject.webUrl,
            downloadUrl: rawObject.downloadUrl,
            lastAccessedTime: itemTimestamp,
            fileType: getFileTypeFromExtension(rawObject.extension),
            sharedBy: rawObject.sharedBy,
            sharingEntityType: rawObject.sharingEntityType,
            previewImageUrl: rawObject.previewImageUrl,
        };
        return object;
    });
    return objects;
}
export function convertRawDataToRecommendedSuggestedTask(rawItem) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    var inclusionReason = rawItem.inclusionReasons && rawItem.inclusionReasons[0];
    var activityUser = ((_a = inclusionReason.actors[0]) === null || _a === void 0 ? void 0 : _a.User) || {};
    var activityDescription = {
        id: '0',
        formatString: activityUser.DisplayName,
        plainText: activityUser.DisplayName,
        people: [
            {
                upn: activityUser.email,
                tag: 'c0',
                name: activityUser.DisplayName,
            },
        ],
        reasonType: ActivityReason.MyAnalytics,
        timestamp: inclusionReason.dateTime
            ? new Date(inclusionReason.dateTime)
            : undefined,
    };
    var descriptionText = '"' + ((_b = rawItem.previewInfo) === null || _b === void 0 ? void 0 : _b.text) + '"';
    var isTeamsTask = rawItem.itemGroups &&
        rawItem.itemGroups.length > 0 &&
        rawItem.itemGroups.indexOf('TeamsTasks') !== -1;
    return {
        app: (_c = rawItem.appInfo) === null || _c === void 0 ? void 0 : _c.name,
        attribution: rawItem.uri,
        docId: undefined,
        driveId: undefined,
        extension: (_d = rawItem.file) === null || _d === void 0 ? void 0 : _d.extension,
        id: rawItem.itemId,
        title: rawItem.name,
        url: isTeamsTask
            ? (_f = (_e = rawItem.taskInsight) === null || _e === void 0 ? void 0 : _e.teamsChatUrl) !== null && _f !== void 0 ? _f : ''
            : rawItem.webUrl,
        webUrl: isTeamsTask
            ? (_h = (_g = rawItem.taskInsight) === null || _g === void 0 ? void 0 : _g.teamsChatUrl) !== null && _h !== void 0 ? _h : ''
            : rawItem.webUrl,
        fileType: FileType.MyAnalytics,
        pathArray: undefined,
        resourceId: rawItem.resource_id,
        isCollab: rawItem.is_collab,
        canonicalUrl: rawItem.uri || rawItem.webUrl,
        feedbackId: rawItem.feedbackId,
        attributionUrl: getFileAttributionUrl(rawItem.uri),
        activity: activityDescription,
        potentialActions: rawItem.potentialActions,
        insightName: rawItem.insightName,
        insightDescription: descriptionText,
        insightDescriptionPrefix: rawItem.taskInsight
            ? getInsightDescriptionPrefix(rawItem.taskInsight.actionType)
            : '',
        actionType: (_j = rawItem.taskInsight) === null || _j === void 0 ? void 0 : _j.actionType,
        isTeamsTask: isTeamsTask,
    };
}
export function convertRawDataToRecommendedCompositeItem(rawItem, officeMarket) {
    var _a;
    var items = rawItem.composition.map(function (rawObject) {
        switch (rawObject.category.toLowerCase()) {
            case 'upcomingmeeting':
                return convertRawDataToRecommendedUpcomingMeeting(rawObject, officeMarket);
            case 'myanalyticsinsight':
                return convertRawDataToRecommendedSuggestedTask(rawObject);
        }
        return undefined;
    });
    var compositeItem = {
        items: items.filter(function (item) {
            return item !== undefined;
        }),
        id: rawItem.itemId,
        title: rawItem.name,
        app: (_a = rawItem.appInfo) === null || _a === void 0 ? void 0 : _a.name,
        attribution: rawItem.uri,
        url: '',
        extension: '',
        feedbackId: rawItem.feedbackId,
    };
    return compositeItem;
}
export function parseRawFeedApiDataToRecommendedData(payload, officeMarket) {
    var _a;
    if (payload && payload.data && payload.data.items) {
        var items = payload.data.items.map(function (rawItem) {
            switch (rawItem.category.toLowerCase()) {
                case 'file':
                case 'video':
                    return convertRawDataToRecommendedFileItem(rawItem);
                case 'upcomingmeeting':
                    return convertRawDataToRecommendedUpcomingMeeting(rawItem, officeMarket);
                case 'myanalyticsinsight':
                    return convertRawDataToRecommendedSuggestedTask(rawItem);
                case 'composite':
                    return convertRawDataToRecommendedCompositeItem(rawItem, officeMarket);
            }
            return undefined;
        });
        return {
            documents: items.filter(function (item) {
                return item !== undefined;
            }),
            diagnosticData: {
                serverRequestId: payload.headers[serverRequestIdHeader],
            },
        };
    }
    return {
        documents: [],
        diagnosticData: {
            serverRequestId: (_a = payload === null || payload === void 0 ? void 0 : payload.headers[serverRequestIdHeader]) !== null && _a !== void 0 ? _a : '',
        },
    };
}
//# sourceMappingURL=parseRawFeedApiDataToRecommendedData.js.map